import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Heading,
  Icon,
  Input,
  NewButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  VStack,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  EHR_OPTIONS,
  MEDICAL_SPECIALTIES,
  ORGANIZATION_SIZE_OPTIONS,
  isKnownSpecialty,
} from '@eluve/utils';

import { singleUserRegistrationDataQuery } from '../../interstitials/UserRegistrationInterstitial';
import { UserRegistrationFormData } from '../../types';

import { userRegistrationFormSchema } from './UserRegistrationFlow';

const isKnownEhr = (ehr: string) =>
  Boolean(EHR_OPTIONS.find((o) => o.value === ehr));

interface UserRegistrationFormProps {
  onSubmit: (data: UserRegistrationFormData) => void;
}

export const UserRegistrationForm: React.FC<UserRegistrationFormProps> = ({
  onSubmit,
}) => {
  const userId = useUserIdFromSession();

  const { data } = useQuery(singleUserRegistrationDataQuery, {
    variables: { id: userId },
  });

  const userData = data?.users[0];
  const form = useForm<UserRegistrationFormData>({
    resolver: zodResolver(userRegistrationFormSchema),
    values: {
      firstName: userData?.firstName ?? '',
      lastName: userData?.lastName ?? '',
      specialty: userData?.specialty ?? '',
      ehrPreference: '',
      organizationSize: '',
    },
  });

  const [isEditingSpecialty, setIsEditingSpecialty] = useState(false);
  const [isEditingEhr, setIsEditingEhr] = useState(false);

  const handleSubmit = (data: UserRegistrationFormData) => {
    onSubmit(data);
  };

  return (
    <VStack gap={6} className="max-w-xl p-8">
      {/* Header */}
      <Heading size="2xl" weight="bold" color="primary">
        Get started with Eluve
      </Heading>

      {/* Form */}
      <Form {...form}>
        <form className="w-full space-y-5">
          {/* Name fields side by side */}
          <HStack gap={4}>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="First Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Last Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </HStack>

          <FormField
            control={form.control}
            name="specialty"
            render={({ field }) => {
              const currentSpecialty = field.value;
              const isOtherCurrent =
                Boolean(currentSpecialty) &&
                !isKnownSpecialty(currentSpecialty);
              return (
                <FormItem>
                  <FormLabel>Specialty</FormLabel>
                  <Combobox>
                    <ComboboxSelectButton
                      className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
                      dropdownIcon={
                        <Icon
                          className="ml-2 opacity-50"
                          name="ChevronDown"
                          size="xs"
                        />
                      }
                    >
                      {isEditingSpecialty || isOtherCurrent
                        ? 'Other'
                        : currentSpecialty || 'Select a specialty'}
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search specialties">
                      {currentSpecialty && (
                        <ComboboxOption
                          onSelect={() => {
                            setIsEditingSpecialty(false);
                            field.onChange('');
                          }}
                        >
                          <Icon className="mr-2" name="X" size="xs" />
                          Clear specialty
                        </ComboboxOption>
                      )}
                      {[...MEDICAL_SPECIALTIES, 'Other'].map((spec) => (
                        <ComboboxOption
                          key={spec}
                          onSelect={() => {
                            if (spec === 'Other') {
                              setIsEditingSpecialty(true);
                              field.onChange('');
                            } else {
                              setIsEditingSpecialty(false);
                              field.onChange(spec);
                            }
                          }}
                        >
                          <ComboboxSelectCheck
                            className="mr-2"
                            selected={
                              isEditingSpecialty || isOtherCurrent
                                ? spec === 'Other'
                                : spec === currentSpecialty
                            }
                          />
                          {spec}
                        </ComboboxOption>
                      ))}
                    </ComboboxDropdown>
                  </Combobox>
                  {(isEditingSpecialty || isOtherCurrent) && (
                    <FormControl>
                      <Input
                        placeholder="Enter your specialty"
                        value={currentSpecialty ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (['Enter', 'Escape'].includes(e.key)) {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="ehrPreference"
            render={({ field }) => {
              const currentEhr = field.value;
              const isOtherCurrent =
                Boolean(currentEhr) && !isKnownEhr(currentEhr);
              const ehrLabel = (value: string) =>
                EHR_OPTIONS.find((o) => o.value === value)?.label ?? value;
              return (
                <FormItem>
                  <FormLabel>EHR</FormLabel>
                  <Combobox>
                    <ComboboxSelectButton
                      className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
                      dropdownIcon={
                        <Icon
                          className="ml-2 opacity-50"
                          name="ChevronDown"
                          size="xs"
                        />
                      }
                    >
                      {isEditingEhr || isOtherCurrent
                        ? 'Other'
                        : currentEhr
                          ? ehrLabel(currentEhr)
                          : 'Select your EHR system'}
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search EHR systems">
                      {currentEhr && (
                        <ComboboxOption
                          onSelect={() => {
                            setIsEditingEhr(false);
                            field.onChange('');
                          }}
                        >
                          <Icon className="mr-2" name="X" size="xs" />
                          Clear EHR
                        </ComboboxOption>
                      )}
                      {[...EHR_OPTIONS.map((o) => o.value)].map((ehr) => (
                        <ComboboxOption
                          key={ehr}
                          onSelect={() => {
                            if (ehr === 'OTHER') {
                              setIsEditingEhr(true);
                              field.onChange('');
                            } else {
                              setIsEditingEhr(false);
                              field.onChange(ehr);
                            }
                          }}
                        >
                          <ComboboxSelectCheck
                            className="mr-2"
                            selected={
                              isEditingEhr || isOtherCurrent
                                ? ehr === 'OTHER'
                                : ehr === currentEhr
                            }
                          />
                          {ehrLabel(ehr)}
                        </ComboboxOption>
                      ))}
                    </ComboboxDropdown>
                  </Combobox>
                  {(isEditingEhr || isOtherCurrent) && (
                    <FormControl>
                      <Input
                        placeholder="Enter your EHR system"
                        value={currentEhr ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (['Enter', 'Escape'].includes(e.key)) {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="organizationSize"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Number of clinicians</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization size" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ORGANIZATION_SIZE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <HStack className="mt-4">
            <NewButton
              type="primary"
              size="m"
              text="Next"
              wFull
              onClick={form.handleSubmit(handleSubmit)}
            />
          </HStack>
        </form>
      </Form>
    </VStack>
  );
};
